// Loads the smart-surveyor agent's cable takeoff (the actual cables that run in
// the trays), produced at agent_output/cables/cables.json on S3. Read-only:
// this is a static agent artifact, mirrored on how fetchResearchReport works.

import { fetchProjectAsset } from "components/SmartSurveyor/SmartSurveyorV3/utils/signedAssetUrls";
import type { CablesPublished, CablesResult, CablePlanTakeoff } from "./types";

// Signed asset URLs are minted fresh per page load and stable within a session,
// so a reload pulls the latest agent output while revisiting a plan still hits
// the browser cache — the same semantics the old ?cb= param provided.
function cablesKey(projectId: string, filename: string): string {
  return `smart-surveyor/projects/${projectId}/agent_output/cables/${filename}`;
}

async function fetchJson<T>(
  projectId: string,
  key: string,
  cache?: "no-store",
): Promise<T | null> {
  try {
    const res = await fetchProjectAsset({ projectId, key, cache });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

/** Fetch the publish gate. Never cached so the on/off state is current. */
export const fetchCablesPublished = async (
  projectId: string,
): Promise<CablesPublished | null> => {
  return fetchJson<CablesPublished>(
    projectId,
    cablesKey(projectId, "published.json"),
    "no-store",
  );
};

/** Fetch the project-level cable takeoff, or null if the agent hasn't produced it.
 * Pass `version` (published_at) to bust the cache when a new estimation is published.
 * Pass `lang` (e.g. "de") to prefer the agent's localized cables_<lang>.json (which
 * carries the provenance / basis-of-estimate text in that language) — mirrors how
 * research_report_<lang>.json works. Falls back to cables.json. */
export const fetchCables = async (
  projectId: string,
  lang?: string,
): Promise<CablesResult | null> => {
  const canonical = await fetchJson<CablesResult>(
    projectId,
    cablesKey(projectId, "cables.json"),
  );
  const base = lang ? lang.slice(0, 2).toLowerCase() : "";
  if (!base || base === "en") return canonical;

  const localized = await fetchJson<CablesResult>(
    projectId,
    cablesKey(projectId, `cables_${base}.json`),
  );
  if (!localized) return canonical;
  if (!canonical) return localized;

  // The localized file is a SEPARATE run, and it drifts. On project 153 the
  // German one was ten days older, had never read the circuit tags off the
  // plan, and so grouped by cable type: 48 circuits and 4,223 m against the
  // canonical 83 circuits and 6,228 m. Switching the interface to German must
  // not change the estimate by 2,000 m. Same run or English numbers.
  const sameRun =
    Boolean(canonical.generated_at) &&
    canonical.generated_at === localized.generated_at;
  return sameRun ? localized : canonical;
};

/** Find the takeoff for a specific plan by matching its (fuzzy) name. */
export function findPlanTakeoff(
  cables: CablesResult | null,
  planName?: string | null,
): CablePlanTakeoff | null {
  if (!cables?.plans?.length || !planName) return null;
  const norm = (s: string) => s.toLowerCase().replace(/[\s_-]/g, "");
  const target = norm(planName);
  return (
    cables.plans.find((p) => norm(p.plan_name) === target) ??
    cables.plans.find(
      (p) =>
        norm(p.plan_name).includes(target) ||
        target.includes(norm(p.plan_name)),
    ) ??
    null
  );
}
